import { fn, hash } from '@ember/helper';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { action, computed, set } from '@ember/object';
import type Owner from '@ember/owner';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert, didUpdate } from '@ember/render-modifiers';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import {
  PrismCheckbox,
  PrismLoadingBar,
  PrismMessage,
} from '@mylighthouse/prism-ember';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { task, type TaskInstance } from 'ember-concurrency';
import { t } from 'ember-intl';
import { and } from 'ember-truth-helpers';
import GeneralCalendarsCalendarRange from 'lumen/components/general/calendars/calendar-range';
import GeneralDatepickersDatepickerWithSidebar, {
  type GeneralDatepickersDatepickerWithSidebarSignature,
} from 'lumen/components/general/datepickers/datepicker-with-sidebar';
import GeneralDropdownsDropdownYield from 'lumen/components/general/dropdowns/dropdown-yield';
import GeneralDropdownsSidebar from 'lumen/components/general/dropdowns/sidebar';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';
import HotelAndMarketRevenueInsightDropdownsTriggersTriggerTarget from 'lumen/components/hotel-and-market/revenue-insight/dropdowns/triggers/trigger-target';
import { timeout } from 'lumen/utils/waiters';

interface HotelAndMarketRevenueInsightDatepickersTargetPickerSignature extends GeneralDatepickersDatepickerWithSidebarSignature {
  Args: GeneralDatepickersDatepickerWithSidebarSignature['Args'] & {
    displayEvents?: boolean;
    hasBudgetForecast: TaskInstance<{
      hasBudgetData: boolean;
      hasForecastData: boolean;
    }> | null;
    nrOfDays: number;
    selectedOptionId: string;
    setHidePickupLY: (value: boolean) => void;
  };
}

interface Option {
  dropdownText: string;
  id: string;
  range?: { end: Dayjs; start: Dayjs };
}

// @ts-expect-error: Incorrect type
export default class HotelAndMarketRevenueInsightDatepickersTargetPicker extends GeneralDatepickersDatepickerWithSidebar<
  HotelAndMarketRevenueInsightDatepickersTargetPickerSignature,
  Option
> {
  @service declare access: Services['access'];
  @service declare riSelections: Services['ri-selections'];
  @service declare usageAnalytics: Services['usage-analytics'];

  // Used to track changes to check if the button should be active or not
  @tracked showPickupLYChanged = false;
  // Used for keeping a temp state for the checkbox
  @tracked tempShowPickupLY: boolean | null = null;

  triggerComponent = HotelAndMarketRevenueInsightDropdownsTriggersTriggerTarget;

  // Made this a task because updating the range causes a rerender. Want to make sure the dropdown is closed first
  updateQueryParamsTask = task(
    { restartable: true },
    async (range, optionId, isPickupLYVisible): Promise<void> => {
      await timeout();

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.args.rangeChanged(range, optionId);

      // Note: it's very important the option gets updated first. Otherwise the beforemodel checks will revert the pickup change
      // We always need to compare vs the riSelections state here since we keep a separate state here.
      // Example: you hide it in budget, switch to SWLY (becomes visible again), then switch to budget again, you want to hide it again without the user needing to change it
      if (
        (optionId === 'budget' || optionId === 'forecast') &&
        this.tempShowPickupLY !== this.showPickupLY
      ) {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        this.args.setHidePickupLY(!isPickupLYVisible);
      }
    },
  );

  get canShowTargetsPickupLY(): boolean {
    return (
      this.riSelections.isMultiProperty ||
      this.access.hasFeature('revenueinsight.targets.show_pickup_ly')
    );
  }

  @computed('args.{hasBudgetForecast.value.hasBudgetData}')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasBudgetTargetsForRange() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.args.hasBudgetForecast?.value?.hasBudgetData;
  }

  @computed('args.{hasBudgetForecast.value.hasForecastData}')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasForecastTargetsForRange() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.args.hasBudgetForecast?.value?.hasForecastData;
  }

  @computed(
    'args.selectedOptionId',
    'convertedRange.{end,start}',
    'showPickupLY',
    'showTargetEmptyState',
    'targetActive',
    'tempOption.id',
    'tempRange.{end,start}',
    'tempShowPickupLY',
  )
  get isApplyDisabled(): boolean {
    // Disable Apply in case of budget / forecast selection when these are not available
    if (
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (this.tempOption?.id === 'budget' ||
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.tempOption?.id === 'forecast') &&
      this.showTargetEmptyState
    ) {
      return true;
    }

    // If we have custom selected, we need to check the ranges, otherwise just check the option ids
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.tempOption?.id !== 'custom') {
      // if the target is currently selected and the checbox state has changed
      // or if the option itself has changed then the button should not be disabled
      return !(
        (this.targetActive &&
          this.tempShowPickupLY !== null &&
          this.showPickupLY !== this.tempShowPickupLY) ||
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.tempOption?.id !== this.args.selectedOptionId
      );
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.convertedRange.start.isSame(this.tempRange?.start) &&
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.convertedRange.end.isSame(this.tempRange?.end)
    );
  }

  get isDisabled(): boolean {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.targetActive || this.riSelections?.isMultiProperty;
  }

  // Actual value used by the checkbox
  get isPickupLYVisible(): boolean {
    // temp value is null by default. If not null anymore, use that value (means it is overwritten)
    return this.tempShowPickupLY ?? this.showPickupLY;
  }

  // Setter used for checkbox value. Actually sets underlying state

  set isPickupLYVisible(value) {
    this.tempShowPickupLY = value;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showPickupLY() {
    return this.riSelections?.showPickupLY;
  }

  @computed(
    'riSelections.isSingleProperty',
    'tempOption.id',
    'hasBudgetTargetsForRange',
    'hasForecastTargetsForRange',
  )
  get showTargetEmptyState(): boolean {
    // Only show the empty state for single property RI
    // @ts-expect-error: Incorrect type
    if (!this.riSelections.isSingleProperty || !this.tempOption) {
      return false;
    }

    return (
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (this.tempOption.id === 'budget' && !this.hasBudgetTargetsForRange) ||
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (this.tempOption.id === 'forecast' && !this.hasForecastTargetsForRange)
    );
  }

  @computed('tempOption.id')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get targetActive() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      // @ts-expect-error: Incorrect type
      this.tempOption &&
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (this.tempOption.id === 'budget' || this.tempOption.id === 'forecast')
    );
  }

  @computed('tempOption.id', 'tempRange')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get triggerValue() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (this.tempOption && this.tempOption.id !== 'custom') {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      return this.tempOption['dropdownText'];
    }

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.tempRange;
  }

  constructor(
    owner: Owner,
    args: HotelAndMarketRevenueInsightDatepickersTargetPickerSignature['Args'],
  ) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    super(owner, args);

    // Need to set a default because it's used for the triggerValue
    // This is not needed in the datepciker becuase we always show a daterange
    set(this, 'tempOption', {
      id: 'swly',
      dropdownText: 'revenue_insight.sdly_short',
    });
  }

  @action
  applyChanges(dropdown: { toggleDropdown: () => void }): void {
    this.trackApplyChanges();

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.updateQueryParamsTask.perform(
      // @ts-expect-error: Incorrect type
      this.tempRange,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.tempOption?.id,
      this.isPickupLYVisible,
    );

    // Reset state
    this.showPickupLYChanged = false;

    dropdown.toggleDropdown();
  }

  @action
  onCancel(): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.updateTempRange(this.convertedRange, this.args.selectedOptionId);
  }

  @action
  selectRange(
    selectedRange: { moment: { start?: Dayjs } },
    calendarShouldRefocus = false,
  ): void {
    let { start } = selectedRange.moment;

    if (!start) {
      return;
    }

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    if (!this.isWithinRange(start)) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      start = this.convertedMinDate;
    }

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { nrOfDays } = this.args;

    const newRange = {
      start,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      end: start.add(nrOfDays, 'day'),
    };

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.updateTempRange(newRange);

    if (calendarShouldRefocus) {
      // @ts-expect-error: Incorrect type
      this.refocusMonths++;
    }
  }

  @action
  trackApplyChanges(): void {
    const isTarget =
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.tempOption?.id === 'budget' || this.tempOption?.id === 'forecast';

    if (isTarget) {
      this.usageAnalytics.track('Target Comparison Changed', {
        eventTrigger: 'target-comparison-picker',
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        targetName: this.tempOption?.id,
      });
    } else {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const isCustomOption = this.tempOption?.id === 'custom';
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const range = isCustomOption ? this.tempRange : this.tempOption?.range;

      this.usageAnalytics.track('Period Comparison Changed', {
        eventTrigger: 'period-comparison-picker',
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        periodName: this.tempOption?.id,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        amountOfDays: range?.end?.diff(range?.start, 'days') ?? 1,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        startDaysOffsetFromToday: range?.start.diff(dayjs(), 'days'),
      });
    }
  }

  @action
  updatePickupLYVisibility(event: Event): void {
    this.isPickupLYVisible = (event.target as HTMLInputElement).checked;
  }

  <template>
    <div
      class="flex-date-picker-container flex flex-row items-center justify-start"
      {{didInsert
        (fn this.updateTempRange this.convertedRange @selectedOptionId)
      }}
      {{didUpdate
        (fn this.updateTempRange this.convertedRange @selectedOptionId)
        @selectedOptionId
      }}
    >
      <GeneralDropdownsDropdownYield
        @dropdownClass=".dropdown-target"
        {{! @glint-expect-error: Incorrect type }}
        @dropdownClassName="dropdown-target"
        @extra={{hash isNetComparison=this.riSelections.isNetComparison}}
        @onCancel={{this.onCancel}}
        @selected={{this.triggerValue}}
        @triggerComponent={{this.triggerComponent}}
        as |dropdown|
      >
        <section class="date-picker-with-side-bar-calendar">
          <section class="date-picker-with-side-bar-calendar__content">
            {{#if this.showTargetEmptyState}}
              <div
                class="dropdown__upload-container flex flex-col items-center justify-center"
              >
                {{#if @hasBudgetForecast.isRunning}}
                  <PrismLoadingBar />
                {{else}}
                  <PrismMessage
                    @description={{t
                      "revenue_insight.empty_state_budget_forecast.description"
                    }}
                    @illustration="100x100_empty"
                    @title={{t
                      "revenue_insight.empty_state_budget_forecast.title"
                    }}
                  />
                {{/if}}
              </div>
            {{else}}
              <div class="mb-500">
                {{! @glint-expect-error: Incorrect type }}
                <GeneralCalendarsCalendarRange
                  @canSelectMonth={{false}}
                  @displayEvents={{@displayEvents}}
                  @isDisabled={{this.isDisabled}}
                  @maxDate={{this.convertedMaxDate}}
                  @maxRange={{@maxRange}}
                  @minDate={{this.convertedMinDate}}
                  @refocusMonths={{this.refocusMonths}}
                  @selectRange={{if this.isDisabled null this.selectRange}}
                  @selectedRange={{this.tempRange}}
                />
                {{#if (and @displayEvents this.access.hasEvents)}}
                  <div class="legend-container text-200--regular">
                    <div class="holiday-or-event-indicator"></div>
                    {{t "revenue_insight.holiday_or_event"}}
                  </div>
                {{else}}
                  {{#if (and this.targetActive this.canShowTargetsPickupLY)}}
                    <div
                      class="legend-container show-pickup-ly flex items-center justify-center"
                    >
                      <PrismCheckbox
                        @checked={{this.isPickupLYVisible}}
                        @onChange={{this.updatePickupLYVisibility}}
                      >
                        {{t "revenue_insight.target_picker.show_pickup_ly"}}
                      </PrismCheckbox>
                    </div>
                  {{/if}}
                  <div class="clear-both h-100"></div>
                {{/if}}
              </div>
            {{/if}}
            <GeneralDropdownsSidebar
              @selectOption={{this.selectOption}}
              @sidebarLists={{this.convertedSidebarLists}}
            />
          </section>
          <section class="date-picker-with-side-bar-calendar__footer">
            <div class="datepicker-save-cancel">
              <GeneralFormFieldsCancelAndConfirm
                @cancel={{fn this.cancelChanges dropdown}}
                @confirm={{fn this.applyChanges dropdown}}
                @isConfirmDisabled={{this.isApplyDisabled}}
              />
            </div>
          </section>
        </section>
      </GeneralDropdownsDropdownYield>
    </div>
  </template>
}
